import { useEffect, useRef } from "react";

import initHotjar from "@/src/utils/hotjarUtils";

import useConsent from "./useConsent";

const isDevEnvironment = (): boolean =>
  import.meta.env.VITE_BAANA_ENVIRONMENT?.toLowerCase() === "dev";

/**
 * Initializes Hotjar according to environment and CMP analytics consent.
 *
 * - local/dev: loads immediately on page load, no consent required (for
 *   testing). preprod/prod ship with an empty `VITE_HOTJAR_ID`, where Hotjar
 *   is driven by GTM instead, so `initHotjar()` is a no-op there.
 * - otherwise: loads only once the user has granted analytics consent, both on
 *   grant within the session and on load for returning consented users.
 *
 * Double initialization is prevented by the `window.hj` guard in
 * `initHotjar()`.
 *
 * Revocation: Hotjar exposes no unload API, so when the user withdraws
 * analytics consent after we loaded Hotjar in the consent-gated path, we reload
 * the page to stop tracking. This is scoped to scripts this hook injected
 * (`loadedByUs`), so the dev path and GTM-driven Hotjar (preprod/prod) never
 * trigger a reload, and a fresh mount without consent never loops.
 */
const useHotjar = (): void => {
  const { hasConsent } = useConsent();
  const loadedByUs = useRef(false);

  useEffect(() => {
    if (isDevEnvironment() || hasConsent("analytics")) {
      if (initHotjar()) {
        loadedByUs.current = true;
      }
      return;
    }

    if (loadedByUs.current) {
      loadedByUs.current = false;
      window.location.reload();
    }
  }, [hasConsent]);
};

export default useHotjar;
